import { Avatar, Box, Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { getInitials } from 'helpers/getInitials'
import { SelfAssessmentInfo } from '../../teacherAssessmentDrawer/components/SelfAssessmentInfo'
import { TeacherAssessmentInput } from '../../teacherAssessmentDrawer/components/TeacherAssessmentInput'
import {
  COL_SELF_ASSESSMENT,
  COL_TEACHER,
  ROW_COMPACT_BREAKPOINT,
  ROW_SECTION_GAP,
} from '../../teacherAssessmentDrawer/components/StudentAssessmentRow'
import { LessonsAssessmentsLink } from './LessonsAssessmentsLink'
import type { FinalAssessmentItem, FinalDrawerStudent } from '../types'

interface FinalStudentRowProps {
  item: FinalAssessmentItem
  student: FinalDrawerStudent
  onScoreChange: (score: number) => void
  onViewLessonsAssessments: () => void
}

/**
 * One student row inside a final-assessment item card. Mirrors the lesson
 * `StudentAssessmentRow` column layout (reusing the same COL_* widths so
 * cards line up) but drops the deliverable column and the per-student enable
 * switch, and adds the "View lessons assessments" slot under the name.
 */
export function FinalStudentRow({ item, student, onScoreChange, onViewLessonsAssessments }: FinalStudentRowProps) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(ROW_COMPACT_BREAKPOINT)
  const ps = item.perStudent[student.id]
  if (!ps) return null

  const color = item.curriculumKind === 'content' ? 'green' : 'blue'
  const name = student.fullName || t('arc_teacher_assessment_drawer.student_label')

  const studentHeader = (
    <Group spacing="sm" noWrap={false} style={{ minWidth: 0, flex: '1 1 0' }} align="flex-start">
      <Avatar src={student.avatarUrl || undefined} radius="xl" size="md">
        {getInitials(student.fullName)}
      </Avatar>
      <Stack spacing={6} style={{ minWidth: 0, flex: 1 }}>
        <Tooltip label={name} withinPortal multiline position="top" width={260}>
          <Text weight={600} size="md" lineClamp={1} style={{ minWidth: 0 }}>
            {name}
          </Text>
        </Tooltip>
        <LessonsAssessmentsLink onClick={onViewLessonsAssessments} />
      </Stack>
    </Group>
  )

  const selfAssessment = (
    <SelfAssessmentInfo studentScore={ps.studentScore} studentAssessed={ps.studentAssessed} />
  )

  const teacherSection = (
    <Stack spacing={6}>
      <Text size="sm" color="dimmed" mb={14} style={{ whiteSpace: 'nowrap' }}>
        {t('arc_teacher_assessment_drawer.teacher_assessment_label')}
      </Text>
      <TeacherAssessmentInput
        value={ps.masterScore}
        onChange={onScoreChange}
        color={color}
        size={isMobile ? 48 : 56}
        rubrics={item.rubrics}
      />
    </Stack>
  )

  if (isMobile) {
    return (
      <Paper withBorder radius="sm" p="md">
        <Stack spacing="md">
          {studentHeader}
          {selfAssessment}
          {teacherSection}
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper withBorder radius="sm" p="md">
      <Box style={{ display: 'flex', alignItems: 'flex-start', gap: ROW_SECTION_GAP, width: '100%' }}>
        {studentHeader}
        <Box style={{ flex: `0 0 ${COL_SELF_ASSESSMENT}px`, minWidth: 0 }}>{selfAssessment}</Box>
        <Box style={{ flex: `0 0 ${COL_TEACHER}px` }}>{teacherSection}</Box>
      </Box>
    </Paper>
  )
}
